(ns frontend.state.updates
  (:require
   [cognitect.transit :as t]
   [common.dates :as dates]
   [common.modules :as modules]
   [common.sync-progress :as sync-progress]
   [common.time :as c-time]
   [common.timer :as timer]
   [common.util :as cu]
   [frontend.db :as app-db]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.shared.state :as shared-state]
   [frontend.state.current-session :as session]
   [frontend.ui.style.theme.core :as theme]
   [frontend.utils :as fu]
   [ui.state.named-values :as named-values]
   [goog.i18n.TimeZone :as goog-tz]
   [re-frame.core :as rf]
   [ui.data.tenant.event :as tenant-event]
   [ui.data.account.event :as account-event]))

(def timeout-atom
  (atom {:last-save      0
         :timeout-handle nil}))

(defn do-store-indexeddb
  [last-known-update last-permissions-ts last-tenant-plan-ts store-to-indexeddb! entity-count]
  (let [now (c-time/now-millis)]
    (println "Store to IndexedDb " last-known-update)
    (when last-known-update
      (swap! app-db/app-state assoc
             :last-known-update last-known-update
             :last-permissions-ts last-permissions-ts
             :last-tenant-plan-ts last-tenant-plan-ts)
      (js/console.log ">last-known-update saved:" last-known-update " << " entity-count))
    (swap! timeout-atom assoc :last-save now)
    (store-to-indexeddb! now)))

(def STORE_INDEXEDDB_AT_LEAST_EVERY_MS 60000)

(defn trigger-store-indexeddb
  [last-known-update last-permissions-ts last-tenant-plan-ts store-to-indexeddb! entity-count]
  (let [now (c-time/now-millis)
        {:keys [last-save timeout-handle]} @timeout-atom]
    (if (or last-known-update
            (> (- now last-save) STORE_INDEXEDDB_AT_LEAST_EVERY_MS))
      (do (when timeout-handle
            ;(js/console.log "Stale + More updates -> clear update timer and INDEX NOW")
            (js/clearTimeout timeout-handle))
          (do-store-indexeddb last-known-update last-permissions-ts last-tenant-plan-ts store-to-indexeddb! entity-count))
      (do (when timeout-handle
            ;(js/console.log "More updates -> clear update timer and set a new one")
            (js/clearTimeout timeout-handle))
          (swap! timeout-atom assoc
                 :timeout-handle
                 (fu/timeout
                  15000
                  (fn [_]
                    (do-store-indexeddb last-known-update last-permissions-ts last-tenant-plan-ts store-to-indexeddb! entity-count))))))))

(defn process-updates-chunk
  [{:keys [reset-session! last? sync-completed! store-to-indexeddb! timer updates deserialize?]}]
  (let [old-sync-progress @session/sync-progress
        updates (if deserialize?
                  (t/read (t/reader :json) updates)
                  updates)
        {:keys [entities data-schema-version reset-db?]} updates
        #_(println "Process updates : " (:last-known-update updates) " > " (keys updates))
        sync-progress-update (:sync-progress updates)
        last-permissions-ts (:last-permissions-ts updates)
        last-tenant-plan-ts (:last-tenant-plan-ts updates)
        last-known-update (:last-known-update updates)
        entities-by-type (->> entities
                              (group-by :entity/type))
        tenant (->> entities-by-type
                    :entity.type/tenant
                    (first))
        timezones (->> entities-by-type
                       :entity.type/timezone)
        accounts (->> entities-by-type
                      :entity.type/account)

        my-account (->> accounts
                        (cu/filter-by :db/id @session/account-id)
                        first)
        my-account-updated-on (:common/updated-on my-account)
        current-my-account @session/account
        current-my-account-updated-on (:common/updated-on current-my-account)
        my-account-newer? (or (not current-my-account-updated-on)
                              (and my-account-updated-on
                                   current-my-account-updated-on
                                   (>= my-account-updated-on current-my-account-updated-on)))
        ;; ensure the current my-account in db/account is not already newer than the
        ;;  my-account we're processing:
        update-my-account? (and my-account
                                my-account-newer?)
        roles (->> entities-by-type
                   :entity.type/role)
        my-updated-role (->> roles
                             (filter (fn [r]
                                       (and (= (:db/id r) @session/role-id)
                                            (= (:role/subject r) (:db/id @session/account)))))
                             (first))]
    (when reset-db?
      ;; Remove all entities, display spinner - rest of the entities will be the ordinary
      ;; full-initial-dataset
      (logging/debug "Resetting frontend db")
      (session/clear-db!)
      (shared-state/clear-indexeddb
       {:account current-my-account
        :tenant  @session/tenant
        :target  @session/target
        :role-id @session/role-id}
       (fn [])
       (fn [_])))

    (when update-my-account?
      (swap! app-db/session assoc
             :account-data-loaded? true
             :account my-account)
      ;; A couple of option lists are read synchronously to default fields on a
      ;; newly created entity, so they cannot be fetched lazily on first read
      ;; the way a dropdown's can - by then the prefill has already run and
      ;; left the field blank. Warm them here, now that the account's data is
      ;; in. A no-op for an account that is bulk-synced them.
      (named-values/warm-create-time-prefills!)
      (deref app-db/session))

    (logging/debug "Before manage entities : " (timer))

    (when tenant
      (swap! session/session assoc :tenant tenant)
      (rf/dispatch [::tenant-event/update-tenant tenant])
      (theme/set-theme! (:tenant/reporting-theme tenant)))

    (when timezones
      (doseq [timezone timezones
              :let [tz-data (:timezone/data timezone)
                    tz-id (:id tz-data)
                    tz (goog-tz/createTimeZone (clj->js tz-data))]]
        (when tz
          (logging/info "Initializing TimeZone - " tz-id)
          (swap! dates/timezones assoc tz-id tz)
          (swap! app-db/app-state assoc-in [:timezone tz-id] tz))))

    (when-let [new-role (:role/role my-updated-role)]
      (when (and (not= :role/sys-admin @session/role)
                 (not (= new-role @session/role)))
        (rf/dispatch [::account-event/set-role new-role])
        (swap! session/session assoc :role new-role)))

    (when (and my-updated-role
               (not= :role/sys-admin @session/role)
               (:common/disabled? my-updated-role))
      ;;TODO should we show error message that role was disabled?
      ;;     and/or logout?
      (logging/info "Logout >> USER DISABLED")
      (logging/warn "user disabled")
      (reset-session!)
      ;;TODO also this?:
      #_(shared-state/clear-indexeddb
         {:account current-my-account
          :tenant  @session/tenant
          :target  @session/target
          :role-id @session/role-id}
         (fn [])
         (fn [_]))

      (let [current-module (session/current-module)
            route (get modules/module->route current-module)]
        (routing/set-view! [(or route :oppos-grid)])))

    (logging/debug "Update processed : " (timer))

    (fu/manage-entities session/reverse-lookup
                        app-db/session
                        entities
                        :keep-disabled #{:entity.type/token :entity.type/role})

    (logging/debug "After manage entities : " (timer))

    ;;TODO selected-*-pipeline reactions created - go through every place where used via keyword & remove below:
    ;; VATAJA think on this track ?
    (when-not @session/selected-oppo-pipeline
      (when-let [first-pipeline (some->> @session/all-oppo-pipelines
                                         (remove :common/disabled?)
                                         (first))]
        (swap! session/selected-pipelines assoc :oppo-pipeline (:db/id first-pipeline))))

    ;; VATAJA think on this track ?
    (when-not @session/selected-froppo-pipeline
      (when-let [first-pipeline (->> @session/all-froppo-pipelines
                                     (remove :common/disabled?)
                                     (first))]
        (swap! session/selected-pipelines assoc :fundraising-opportunity-pipeline (:db/id first-pipeline))))

    ;; VATAJA think on this track ?
    (when-not @session/selected-sales-oppo-pipeline
      (when-let [first-pipeline (->> @session/all-sales-oppo-pipelines
                                     (remove :common/disabled?)
                                     (first))]
        (swap! session/selected-pipelines assoc :sales-opportunity-pipeline (:db/id first-pipeline))))

    (when-not @session/selected-exit-oppo-pipeline
      (when-let [first-pipeline (->> @session/exit-oppo-pipelines
                                     (first))]
        (swap! session/selected-pipelines assoc :exit-opportunity-pipeline (:db/id first-pipeline))))

    (when-not @session/tenant-data-loaded?
      (swap! app-db/app-state assoc :start-time (c-time/now-millis))
      (swap! session/session assoc :tenant-data-loaded? true)
      (session/load-defaults))

    (when sync-progress-update
      (if (= old-sync-progress sync-progress/COMPLETE)
        (logging/debug "Do not store sync progress -> UI using cached version as completed")
        (fu/timeout 10 #(swap! session/session assoc :sync-progress sync-progress-update))))

    (logging/debug "Before sync-completed : " (timer) " << " update-my-account?)

    (when (and (fn? sync-completed!)
               (= sync-progress/COMPLETE sync-progress-update))
      (sync-completed!))

    (when (and (or last?
                   (= sync-progress-update sync-progress/COMPLETE))
               (fn? store-to-indexeddb!)
               (or (not my-account)
                   update-my-account?))
      (trigger-store-indexeddb last-known-update
                               last-permissions-ts
                               last-tenant-plan-ts
                               store-to-indexeddb!
                               (count entities)))

    (logging/debug "Processed SYNC updates : " (timer))

    ;(js/console.log "Process chunk end : " (fn? store-to-indexeddb!) " << " update-my-account?)
    :ok))

(def queued-updates (atom []))

(defn process-queued-updates
  []
  (let [[queued _] (reset-vals! queued-updates [])]
    #_(println "Process queued updates " (count queued))
    (->> queued
         (map-indexed (fn [idx {:keys [reset-session! store-to-indexeddb! sync-completed! timer updates deserialize?]}]
                        (process-updates-chunk {:reset-session!      reset-session!
                                                :last?               (= idx (dec (count queued)))
                                                :sync-completed!     sync-completed!
                                                :store-to-indexeddb! store-to-indexeddb!
                                                :timer               timer
                                                :updates             updates
                                                :deserialize?        deserialize?})
                        nil))
         (doall))))

(defn process-updates
  [{:keys [reset-session! sync-completed! timer updates deserialize? from-indexeddb?] :as args}]
  (let [timer (if (fn? timer)
                timer
                (timer/create-timer))]
    #_(logging/debug "Received SYNC : " (timer) " > " (count (:entities updates)))
    (swap! queued-updates conj (assoc args :timer timer))
    (process-queued-updates)))
